package com.evoapps.evoshift.domain.usecase.requests

import com.evoapps.evoshift.domain.model.Absence
import com.evoapps.evoshift.domain.model.AbsenceStatus
import com.evoapps.evoshift.domain.model.Roster
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.SwapRequest
import com.evoapps.evoshift.domain.model.SwapStatus
import com.evoapps.evoshift.domain.repository.AbsenceRepository
import com.evoapps.evoshift.domain.repository.RosterRepository
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.repository.WorkRequestRepository
import com.evoapps.evoshift.domain.model.WorkRequest
import com.evoapps.evoshift.domain.util.AppClock
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.onStart
import kotlinx.datetime.LocalDate

/** SH-164: a swap a manager has to decide: a give-away to let onto the marketplace, or a claim to confirm. */
fun SwapRequest?.awaitsManager(): Boolean =
    this?.status == SwapStatus.REQUESTED_BY_OWNER || this?.status == SwapStatus.TAKEN_PENDING

/** SH-164: what waits for a manager's decision at one workplace. */
data class PendingRequests(
    /** Absence requests still PENDING, newest start first. */
    val absences: List<Absence> = emptyList(),
    /** Shifts whose swap request [awaitsManager], by planned start. */
    val swaps: List<Shift> = emptyList(),
    /** SH-223: remote work, work events and overtime still PENDING. */
    val work: List<WorkRequest> = emptyList()
) {
    val count: Int get() = absences.size + swaps.size + work.size
}

/**
 * SH-164: the workplace's requests waiting for a manager, live: the Requests screen's list, the
 * badges of its tab (or of More, where Requests lives for money roles) and the dashboards' inbox
 * card all count the same ones.
 *
 * Swaps live on shifts, read per roster (the Firestore rules prove access through the roster id):
 * only the months from the current one on, as a past shift can no longer change hands. Absences:
 * every pending one (sick leave is often reported afterwards).
 */
class ObservePendingRequestsUseCase(
    private val rosterRepository: RosterRepository,
    private val shiftRepository: ShiftRepository,
    private val absenceRepository: AbsenceRepository,
    /** SH-223: the work requests; without it none. */
    private val workRequestRepository: WorkRequestRepository? = null,
    private val today: () -> LocalDate = { AppClock.today() }
) {
    @OptIn(ExperimentalCoroutinesApi::class)
    fun swaps(workplaceId: String): Flow<List<Shift>> =
        rosterRepository.observeRosters(workplaceId)
            .map { rosters -> currentAndLater(rosters).map { it.id }.distinct() }
            .distinctUntilChanged()
            .flatMapLatest { ids ->
                if (ids.isEmpty()) flowOf(emptyList())
                else combine(ids.map { shiftRepository.observePendingSwaps(it) }) { perRoster -> perRoster.toList().flatten() }
            }
            .map { shifts -> shifts.filter { it.swapRequest.awaitsManager() }.sortedBy { it.plannedTime.start } }
            .distinctUntilChanged()

    fun absences(workplaceId: String): Flow<List<Absence>> =
        absenceRepository.observeAbsencesForWorkplace(workplaceId)
            .map { all -> all.filter { it.status == AbsenceStatus.PENDING } }
            .distinctUntilChanged()

    /** SH-223: the work requests waiting for a decision. */
    fun work(workplaceId: String): Flow<List<WorkRequest>> =
        (workRequestRepository?.observeForWorkplace(workplaceId) ?: flowOf(emptyList()))
            .map { all -> all.filter { it.status == AbsenceStatus.PENDING } }
            .distinctUntilChanged()

    /** All together; a failed read counts as nothing to decide (a badge never shows an error). */
    operator fun invoke(workplaceId: String): Flow<PendingRequests> =
        combine(
            absences(workplaceId).onStart { emit(emptyList()) }.catch { emit(emptyList()) },
            swaps(workplaceId).onStart { emit(emptyList()) }.catch { emit(emptyList()) },
            work(workplaceId).onStart { emit(emptyList()) }.catch { emit(emptyList()) }
        ) { absences, swaps, work -> PendingRequests(absences, swaps, work) }
            .distinctUntilChanged()

    private fun currentAndLater(rosters: List<Roster>): List<Roster> {
        val now = today()
        val current = now.year * 12 + now.month.ordinal
        return rosters.filter { it.year * 12 + (it.month - 1) >= current }
    }
}
